import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
// DTC-L: route org-level KPI window anchors through clock.
import { clock } from '@/utils/clock';
import { useAuth } from '@/contexts/AuthContext';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { calculateOBLI, getOBLIColorClasses } from '@/utils/obliCalculation';
import { useNetworkSignals } from '@/hooks/useNetworkSignals';
import { useNetworkLearning } from '@/hooks/useNetworkLearning';
import { StatCard } from '@/components/dashboard/StatCard';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import {
  Building2, ArrowUpDown, Store, ArrowRight, ArrowUp, ArrowDown, Minus,
  AlertTriangle, TrendingDown, Clock, Zap, Activity, BarChart3, Users
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

// ── Types ──────────────────────────────────────────────

type DateRange = 'today' | 'yesterday' | '7d';

interface PerShop {
  shop_id: string;
  shop_name: string;
  address: string | null;
  active_tickets: number;
  completed_in_range: number;
  booked_minutes: number;
  waiter_count: number;
  total_techs: number;
  total_capacity: number;
}

interface DashboardMetrics {
  total_shops: number;
  active_tickets: number;
  completed_in_range: number;
  vehicles_in_shop: number;
  estimated_revenue: number | null;
  total_booked_minutes: number;
  total_capacity_minutes: number;
  per_shop: PerShop[];
}

interface ShopWithIntel extends PerShop {
  obli: ReturnType<typeof calculateOBLI>;
  utilization: number;
  waiterLoad: number;
  capacityStatus: string;
  priorObli: number | null;
  trend: 'up' | 'down' | 'flat';
}

interface OpportunityFlag {
  type: 'OVERLOADED' | 'UNDERUTILIZED' | 'COMPLETION_DROP' | 'WAITER_PRESSURE';
  shopName: string;
  message: string;
  severity: 'high' | 'medium' | 'low';
}

type SortKey = 'obli' | 'active_tickets' | 'completed_in_range' | 'utilization' | 'waiterLoad';

// ── Helpers ────────────────────────────────────────────

// IV-D-22 R73 FIX-3: use browser-local calendar date for enterprise-wide
// "today"/"yesterday"/"7d" windows. Previously `toISOString().slice(0,10)`
// returned the UTC date, so a user in PST viewing the org dashboard at
// 9 PM local saw UTC's "tomorrow" — the recommendations panel loaded a day
// that hadn't happened anywhere in the org yet. `format(d, 'yyyy-MM-dd')`
// honors the viewer's local clock, which is the closest available proxy
// for enterprise rollup where shops span multiple timezones.
function getDateRange(range: DateRange): { start: string; end: string } {
  const today = clock.now();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  if (range === 'yesterday') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { start: fmt(y), end: fmt(y) };
  }
  if (range === '7d') {
    const w = new Date(today);
    w.setDate(w.getDate() - 6);
    return { start: fmt(w), end: fmt(today) };
  }
  return { start: fmt(today), end: fmt(today) };
}

function getPriorDateRange(range: DateRange): { start: string; end: string } {
  const today = clock.now();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  if (range === 'today') {
    const y = new Date(today);
    y.setDate(y.getDate() - 1);
    return { start: fmt(y), end: fmt(y) };
  }
  if (range === 'yesterday') {
    const d = new Date(today);
    d.setDate(d.getDate() - 2);
    return { start: fmt(d), end: fmt(d) };
  }
  // 7d → prior 7 days
  const priorEnd = new Date(today);
  priorEnd.setDate(priorEnd.getDate() - 7);
  const priorStart = new Date(priorEnd);
  priorStart.setDate(priorStart.getDate() - 6);
  return { start: fmt(priorStart), end: fmt(priorEnd) };
}

function shopOBLI(shop: PerShop) {
  return calculateOBLI({
    totalCapacity: shop.total_capacity || 0,
    bookedMinutes: shop.booked_minutes,
    ticketCount: shop.active_tickets,
    waiterCount: shop.waiter_count,
    specialtyJobCount: 0,
    workingTechCount: shop.total_techs,
    totalTechCount: shop.total_techs,
    isShopOpen: true,
  });
}

function getCapacityStatus(obliScore: number): string {
  if (obliScore > 85) return 'Overloaded';
  if (obliScore > 60) return 'Busy';
  if (obliScore > 25) return 'Balanced';
  return 'Light';
}

function getCapacityStatusColor(status: string) {
  switch (status) {
    case 'Overloaded': return 'bg-destructive/20 text-destructive';
    case 'Busy': return 'bg-orange-500/20 text-orange-600';
    case 'Balanced': return 'bg-warning/20 text-warning';
    case 'Light': return 'bg-success/20 text-success';
    default: return 'bg-muted text-muted-foreground';
  }
}

function getTrend(current: number, prior: number | null): 'up' | 'down' | 'flat' {
  if (prior === null) return 'flat';
  if (current > prior + 5) return 'up';
  if (current < prior - 5) return 'down';
  return 'flat';
}

function TrendIcon({ trend }: { trend: 'up' | 'down' | 'flat' }) {
  if (trend === 'up') return <ArrowUp className="h-4 w-4 text-destructive" />;
  if (trend === 'down') return <ArrowDown className="h-4 w-4 text-success" />;
  return <Minus className="h-4 w-4 text-muted-foreground" />;
}

function getFlagIcon(type: OpportunityFlag['type']) {
  switch (type) {
    case 'OVERLOADED': return <AlertTriangle className="h-4 w-4 text-destructive" />;
    case 'UNDERUTILIZED': return <TrendingDown className="h-4 w-4 text-blue-500" />;
    case 'COMPLETION_DROP': return <TrendingDown className="h-4 w-4 text-warning" />;
    case 'WAITER_PRESSURE': return <Clock className="h-4 w-4 text-orange-500" />;
  }
}

function getFlagBorder(type: OpportunityFlag['type']) {
  switch (type) {
    case 'OVERLOADED': return 'border-destructive/30';
    case 'UNDERUTILIZED': return 'border-blue-500/30';
    case 'COMPLETION_DROP': return 'border-warning/30';
    case 'WAITER_PRESSURE': return 'border-orange-500/30';
  }
}

// ── Component ──────────────────────────────────────────

const OrgIntelligence = () => {
  const navigate = useNavigate();
  const { user, activeShop, setActiveShop } = useAuth();
  const { canViewOrgDashboard, scopeLabel } = useEnterpriseScope();
  const { syncSignals } = useNetworkSignals();
  const { recordEvent } = useNetworkLearning();

  const [currentMetrics, setCurrentMetrics] = useState<DashboardMetrics | null>(null);
  const [priorMetrics, setPriorMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<DateRange>('today');
  const [sortKey, setSortKey] = useState<SortKey>('obli');
  const [sortAsc, setSortAsc] = useState(false);

  // Fetch current + prior period data
  useEffect(() => {
    if (!user?.id || !canViewOrgDashboard) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const current = getDateRange(dateRange);
        const prior = getPriorDateRange(dateRange);

        const [currentRes, priorRes] = await Promise.all([
          supabase.rpc('get_org_dashboard_metrics', {
            _user_id: user.id,
            _start_date: current.start,
            _end_date: current.end,
          }),
          supabase.rpc('get_org_dashboard_metrics', {
            _user_id: user.id,
            _start_date: prior.start,
            _end_date: prior.end,
          }),
        ]);

        if (currentRes.error) console.error('[OrgIntelligence] Current RPC error:', currentRes.error);
        if (priorRes.error) console.error('[OrgIntelligence] Prior RPC error:', priorRes.error);

        setCurrentMetrics(currentRes.data as unknown as DashboardMetrics ?? null);
        setPriorMetrics(priorRes.data as unknown as DashboardMetrics ?? null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id, canViewOrgDashboard, dateRange]);

  // Build enriched shop data
  const shops = useMemo((): ShopWithIntel[] => {
    if (!currentMetrics?.per_shop) return [];

    const priorMap = new Map<string, PerShop>();
    priorMetrics?.per_shop?.forEach((s) => priorMap.set(s.shop_id, s));

    return currentMetrics.per_shop.map((s) => {
      const obli = shopOBLI(s);
      const utilization = s.total_capacity > 0
        ? Math.round((s.booked_minutes / s.total_capacity) * 100) : 0;
      const waiterLoad = s.active_tickets > 0
        ? Math.round((s.waiter_count / s.active_tickets) * 100) : 0;

      const prior = priorMap.get(s.shop_id);
      const priorObliScore = prior ? shopOBLI(prior).score : null;

      return {
        ...s,
        obli,
        utilization,
        waiterLoad,
        capacityStatus: getCapacityStatus(obli.score),
        priorObli: priorObliScore,
        trend: getTrend(obli.score, priorObliScore),
      };
    });
  }, [currentMetrics, priorMetrics]);

  // Sorted shops
  const sortedShops = useMemo(() => {
    const sorted = [...shops];
    sorted.sort((a, b) => {
      let av: number, bv: number;
      switch (sortKey) {
        case 'obli': av = a.obli.score; bv = b.obli.score; break;
        case 'active_tickets': av = a.active_tickets; bv = b.active_tickets; break;
        case 'completed_in_range': av = a.completed_in_range; bv = b.completed_in_range; break;
        case 'utilization': av = a.utilization; bv = b.utilization; break;
        case 'waiterLoad': av = a.waiterLoad; bv = b.waiterLoad; break;
        default: av = a.obli.score; bv = b.obli.score;
      }
      return sortAsc ? av - bv : bv - av;
    });
    return sorted;
  }, [shops, sortKey, sortAsc]);

  // Network summary
  const summary = useMemo(() => {
    if (shops.length === 0) return null;

    const highest = shops.reduce((max, s) => s.obli.score > max.obli.score ? s : max, shops[0]);
    const lowest = shops.reduce((min, s) => s.obli.score < min.obli.score ? s : min, shops[0]);
    const avgUtil = Math.round(shops.reduce((s, sh) => s + sh.utilization, 0) / shops.length);
    const aboveThreshold = shops.filter((s) => s.obli.score > 85).length;

    return { highest, lowest, avgUtil, aboveThreshold };
  }, [shops]);

  // Opportunity flags
  const flags = useMemo((): OpportunityFlag[] => {
    const result: OpportunityFlag[] = [];
    const priorMap = new Map<string, PerShop>();
    priorMetrics?.per_shop?.forEach((s) => priorMap.set(s.shop_id, s));

    shops.forEach((s) => {
      // Overloaded
      if (s.obli.score > 85 || s.utilization > 90) {
        result.push({
          type: 'OVERLOADED',
          shopName: s.shop_name,
          message: `${s.shop_name} appears overloaded — OBLI ${s.obli.score}, ${s.utilization}% utilization`,
          severity: 'high',
        });
      }

      // Underutilized
      if (s.obli.score < 20 && s.utilization < 30) {
        result.push({
          type: 'UNDERUTILIZED',
          shopName: s.shop_name,
          message: `${s.shop_name} has low utilization and available capacity — OBLI ${s.obli.score}, ${s.utilization}% utilized`,
          severity: 'low',
        });
      }

      // Completion drop
      const prior = priorMap.get(s.shop_id);
      if (prior && prior.completed_in_range > 0) {
        if (s.completed_in_range < prior.completed_in_range * 0.75) {
          result.push({
            type: 'COMPLETION_DROP',
            shopName: s.shop_name,
            message: `${s.shop_name} completion volume is down vs prior period (${s.completed_in_range} vs ${prior.completed_in_range})`,
            severity: 'medium',
          });
        }
      }

      // Waiter pressure
      if (s.active_tickets > 0 && s.waiter_count / s.active_tickets > 0.4) {
        result.push({
          type: 'WAITER_PRESSURE',
          shopName: s.shop_name,
          message: `${s.shop_name} has elevated waiter load — ${s.waiter_count} waiters of ${s.active_tickets} tickets (${s.waiterLoad}%)`,
          severity: 'medium',
        });
      }
    });

    // Sort by severity
    const severityOrder = { high: 0, medium: 1, low: 2 };
    result.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
    return result;
  }, [shops, priorMetrics]);

  // Sync flags to persistent signals
  useEffect(() => {
    if (flags.length === 0 && !currentMetrics) return;
    const forSync = flags.map((f) => {
      const shop = shops.find((s) => s.shop_name === f.shopName);
      return {
        signal_type: f.type === 'OVERLOADED' ? 'OVERLOAD_ATTENTION'
          : f.type === 'COMPLETION_DROP' ? 'COMPLETION_WEAKNESS'
          : f.type,
        shop_id: shop?.shop_id,
        severity: f.severity,
        priority_score: shop ? shop.obli.score : 50,
        title: `${f.shopName} — ${f.type.replace(/_/g, ' ').toLowerCase()}`,
        summary: f.message,
        reasons: [f.message],
      };
    });
    syncSignals(forSync);
  }, [flags]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectShop = async (shopId: string) => {
    recordEvent('intelligence', null, 'intelligence_shop_drilldown', shopId);
    await setActiveShop(shopId);
    navigate('/');
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const SortButton = ({ label, field }: { label: string; field: SortKey }) => (
    <button
      className="flex items-center gap-1 hover:text-foreground"
      onClick={() => handleSort(field)}
    >
      {label} <ArrowUpDown className="h-3 w-3" />
    </button>
  );

  // ── Guards ─────────────────────────────────────────

  if (!canViewOrgDashboard) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="mb-4 text-4xl font-bold">403 - Forbidden</h1>
          <p className="text-xl text-muted-foreground">
            You don't have permission to access this page.
          </p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────

  const comparisonLabel = dateRange === 'today' ? 'vs Yesterday'
    : dateRange === 'yesterday' ? 'vs Day Before'
    : 'vs Prior 7 Days';

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="mx-auto max-w-7xl space-y-6">

        {/* Stage 2 — back to OptiNetwork hub. */}
        <BackToOptiNetworkButton testId="org-intelligence-back-to-opti-network" />

        {/* ── Header ─────────────────────────────── */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <Zap className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold text-foreground">
                {scopeLabel} Intelligence
              </h1>
              <p className="text-sm text-muted-foreground">
                Cross-shop comparison & outlier detection · {comparisonLabel}
              </p>
              {/* Phase 8 — deferral note. Backed by `get_org_dashboard_metrics`,
                  which does not accept a scope filter. The Phase 6 scope
                  switcher selection is not (yet) reflected here. */}
              <p className="text-xs italic text-muted-foreground mt-1">
                Showing organization-level data. Region- and district-level
                scope-aware intelligence is deferred to a future phase.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/org')}>
              ← Dashboard
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/org/recommendations')}>
              Recommendations →
            </Button>
            <div className="flex gap-1">
              {([
                ['today', 'Today'],
                ['yesterday', 'Yesterday'],
                ['7d', 'Last 7 Days'],
              ] as [DateRange, string][]).map(([key, label]) => (
                <Button
                  key={key}
                  size="sm"
                  variant={dateRange === key ? 'default' : 'outline'}
                  onClick={() => setDateRange(key)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Loading intelligence…</div>
          </div>
        ) : !currentMetrics ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-lg text-muted-foreground">Failed to load data</div>
          </div>
        ) : (
          <>
            {/* ── Section 1: Network Summary Cards ──── */}
            {summary && (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                <StatCard
                  title="Highest OBLI"
                  value={summary.highest.obli.score}
                  subtitle={summary.highest.shop_name}
                  icon={Activity}
                />
                <StatCard
                  title="Lowest OBLI"
                  value={summary.lowest.obli.score}
                  subtitle={summary.lowest.shop_name}
                  icon={BarChart3}
                />
                <StatCard
                  title="Avg Utilization"
                  value={`${summary.avgUtil}%`}
                  icon={Users}
                />
                <StatCard
                  title="Shops Above 85"
                  value={summary.aboveThreshold}
                  subtitle={`of ${shops.length} shops`}
                  icon={AlertTriangle}
                />
              </div>
            )}

            {/* ── Section 2: Outlier & Opportunity Panel ── */}
            {flags.length > 0 && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="h-5 w-5 text-primary" />
                    Signals & Opportunities
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {flags.map((flag, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex items-start gap-3 rounded-md border p-3',
                        getFlagBorder(flag.type),
                      )}
                    >
                      {getFlagIcon(flag.type)}
                      <p className="text-sm text-foreground">{flag.message}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {flags.length === 0 && shops.length > 0 && (
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Activity className="h-5 w-5 text-success" />
                  <p className="text-sm text-muted-foreground">
                    No outliers detected — all shops within normal operating range.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* ── Section 3: Shop Comparison Table ──── */}
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Shop</TableHead>
                    <TableHead><SortButton label="OBLI" field="obli" /></TableHead>
                    <TableHead><SortButton label="Active" field="active_tickets" /></TableHead>
                    <TableHead><SortButton label="Completed" field="completed_in_range" /></TableHead>
                    <TableHead><SortButton label="Utilization" field="utilization" /></TableHead>
                    <TableHead><SortButton label="Waiter Load" field="waiterLoad" /></TableHead>
                    <TableHead>Capacity</TableHead>
                    <TableHead>Trend</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedShops.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                        No shops found
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedShops.map((shop) => {
                      const colors = getOBLIColorClasses(shop.obli.score);
                      return (
                        <TableRow
                          key={shop.shop_id}
                          className={cn(
                            'cursor-pointer transition-colors hover:bg-accent/50',
                            activeShop?.id === shop.shop_id && 'bg-accent/30',
                          )}
                          onClick={() => handleSelectShop(shop.shop_id)}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Store className="h-4 w-4 text-muted-foreground shrink-0" />
                              <div>
                                <p className="font-medium">{shop.shop_name}</p>
                                {shop.address && (
                                  <p className="text-xs text-muted-foreground truncate max-w-[180px]">{shop.address}</p>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={cn(colors.bg, colors.text, 'border-0')}>
                              {shop.obli.score}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-medium">{shop.active_tickets}</TableCell>
                          <TableCell>{shop.completed_in_range}</TableCell>
                          <TableCell>{shop.utilization}%</TableCell>
                          <TableCell>{shop.waiterLoad}%</TableCell>
                          <TableCell>
                            <Badge className={cn(getCapacityStatusColor(shop.capacityStatus), 'border-0')}>
                              {shop.capacityStatus}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <TrendIcon trend={shop.trend} />
                          </TableCell>
                          <TableCell>
                            <ArrowRight className="h-4 w-4 text-muted-foreground" />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default OrgIntelligence;
